import React, { useState, useEffect, useRef } from "react";
import { LogOut, AlertTriangle, Lock, CheckCircle, Bell, Eye, EyeOff, Camera, Upload, X } from "lucide-react";
import api from "../../api";
import { uploadUrl } from "../../uploadUrl";
import { K } from "../../theme";
import { Btn, Card, Pill, Field, TextInput } from "../components";
import { subscribePush, unsubscribePush } from "../push";
import { validateFaceDetectable } from "../../utils/faceValidate";

// Clamp crop offset so image edges always cover the circle (no white space).
function clampMobileCrop(state) {
  if (!state) return state;
  const cropSize = 220;
  const bs = state.baseScale || 1;
  const z = state.zoom || 1;
  const w = (state.imgW || cropSize) * bs * z;
  const h = (state.imgH || cropSize) * bs * z;
  const maxX = Math.max(0, (w - cropSize) / 2);
  const maxY = Math.max(0, (h - cropSize) / 2);
  return {
    ...state,
    offsetX: Math.max(-maxX, Math.min(maxX, state.offsetX || 0)),
    offsetY: Math.max(-maxY, Math.min(maxY, state.offsetY || 0)),
  };
}

// Self-service photo upload — file picker or live camera capture, mirrors
// the desktop My Profile modal. Drag-to-reposition needs touch handlers
// here (not just mouse) since this is the mobile view.
function MobilePhotoUploadModal({ onClose, onSaved }) {
  const cropSize = 220;
  const [crop, setCrop] = useState({ src: null, zoom: 1, offsetX: 0, offsetY: 0, dragging: false, file: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [camError, setCamError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cropperRef = useRef(null);

  useEffect(() => () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); }, []);

  const onFileSelect = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.src = ev.target.result;
      img.onload = () => {
        const baseScale = cropSize / Math.min(img.width, img.height);
        setCrop(p => ({ ...p, src: ev.target.result, file, zoom: 1, offsetX: 0, offsetY: 0, imgW: img.width, imgH: img.height, baseScale }));
      };
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 480 } } });
      streamRef.current = stream;
      setCamOpen(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } }, 0);
    } catch (e) { setCamError(e.name === "NotAllowedError" ? "Camera access denied. Allow camera access and try again." : "Could not access camera: " + e.message); }
  };
  const stopCamera = () => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } setCamOpen(false); };
  const capturePhoto = () => {
    const v = videoRef.current; if (!v) return;
    const c = document.createElement("canvas"); c.width = v.videoWidth || 480; c.height = v.videoHeight || 480;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => { if (blob) onFileSelect(new File([blob], "camera-photo.jpg", { type: "image/jpeg" })); stopCamera(); }, "image/jpeg", 0.9);
  };

  const dragStart = (x, y) => setCrop(p => ({ ...p, dragging: true, _startX: x - p.offsetX, _startY: y - p.offsetY }));
  const dragMove = (x, y) => setCrop(p => p.dragging ? clampMobileCrop({ ...p, offsetX: x - p._startX, offsetY: y - p._startY }) : p);
  const dragEnd = () => setCrop(p => ({ ...p, dragging: false }));

  const doSave = async () => {
    if (!crop.src) return;
    setBusy(true); setErr(null);
    try {
      const c = document.createElement("canvas"); c.width = 300; c.height = 300;
      const ctx = c.getContext("2d");
      const img = new Image(); img.src = crop.src;
      await new Promise(r => { img.onload = r; });
      const bs = crop.baseScale || 1; const z = crop.zoom; const totalScale = bs * z;
      const drawW = img.width * totalScale; const drawH = img.height * totalScale;
      const outScale = 300 / cropSize;
      const dx = (cropSize / 2 - drawW / 2 + crop.offsetX) * outScale;
      const dy = (cropSize / 2 - drawH / 2 + crop.offsetY) * outScale;
      ctx.beginPath(); ctx.arc(150, 150, 150, 0, Math.PI * 2); ctx.clip();
      ctx.drawImage(img, dx, dy, drawW * outScale, drawH * outScale);
      const faceCheck = await validateFaceDetectable(c);
      if (!faceCheck.ok) { setErr(faceCheck.message); setBusy(false); return; }
      const blob = await new Promise(r => c.toBlob(r, "image/jpeg", 0.9));
      const fd = new FormData(); fd.append("file", blob, "profile-me.jpg");
      await api.upload("/api/employees/me/profile-photo", fd);
      onSaved?.();
      onClose();
    } catch (e) { setErr(e.message || "Upload failed"); setBusy(false); }
  };

  return <div className="fixed inset-0 animate-fadeIn bg-black/60 z-[300] flex items-center justify-center p-3" onMouseDown={() => { stopCamera(); onClose(); }}>
    <div className="animate-modalIn bg-bg1 rounded-[20px] p-5 w-full max-w-[600px] max-h-[90vh] overflow-auto shadow-[0_24px_80px_rgba(0,0,0,0.25)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-5">
        <div>
          <div className="text-base font-extrabold text-t0">Update Photo</div>
          <div className="text-[14px] text-t3">{camOpen ? "Center your face and capture" : "Drag to reposition · Pinch/scroll to zoom"}</div>
        </div>
        <button onClick={() => { stopCamera(); onClose(); }} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
      </div>
      {err && <div className="text-[14px] text-r mb-2">{err}</div>}
      {!crop.src && !camOpen ? (
        <div>
          <div
            className="border-2 border-dashed border-b1 rounded-2xl p-10 text-center cursor-pointer bg-bg2"
            onClick={() => { const inp = document.createElement("input"); inp.type = "file"; inp.accept = ".jpg,.jpeg,.png,.webp"; inp.capture = "environment"; inp.onchange = e => { const f = e.target.files[0]; if (f) onFileSelect(f); }; inp.click(); }}
          >
            <Upload size={32} color={K.p} className="mx-auto mb-3" />
            <div className="text-[15px] font-bold text-t0 mb-1">Choose a photo</div>
            <div className="text-[14px] text-t3">JPG, PNG, WebP</div>
          </div>
          <div className="flex items-center gap-2 my-3"><div className="flex-1 h-px bg-b1" /><span className="text-[12px] text-t3">OR</span><div className="flex-1 h-px bg-b1" /></div>
          <Btn variant="secondary" full icon={Camera} onClick={startCamera}>Take Photo</Btn>
          {camError && <div className="text-[14px] mt-2 text-center" style={{ color: K.r }}>{camError}</div>}
        </div>
      ) : camOpen ? (
        <div className="flex flex-col items-center">
          <div className="rounded-full overflow-hidden border-[3px] border-p bg-bg2" style={{ width: cropSize, height: cropSize }}>
            <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
          </div>
          <div className="flex gap-2 mt-4 w-full">
            <Btn variant="secondary" full onClick={stopCamera}>Cancel</Btn>
            <Btn full icon={Camera} onClick={capturePhoto}>Capture</Btn>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex flex-col items-center mb-4">
            <div
              ref={cropperRef}
              className="rounded-full overflow-hidden relative cursor-grab select-none border-[3px] border-p touch-none"
              style={{ width: cropSize, height: cropSize, boxShadow: `0 0 0 4px ${K.p}20` }}
              onMouseDown={e => { e.preventDefault(); dragStart(e.clientX, e.clientY); }}
              onMouseMove={e => dragMove(e.clientX, e.clientY)}
              onMouseUp={dragEnd}
              onMouseLeave={dragEnd}
              onTouchStart={e => { const t = e.touches[0]; dragStart(t.clientX, t.clientY); }}
              onTouchMove={e => { const t = e.touches[0]; dragMove(t.clientX, t.clientY); }}
              onTouchEnd={dragEnd}
            >
              {(() => {
                const bs = crop.baseScale || 1; const z = crop.zoom;
                const w = (crop.imgW || cropSize) * bs * z;
                const h = (crop.imgH || cropSize) * bs * z;
                return <img src={crop.src} alt="crop" draggable={false} className="absolute pointer-events-none" style={{ left: cropSize / 2 - w / 2 + crop.offsetX, top: cropSize / 2 - h / 2 + crop.offsetY, width: w, height: h, maxWidth: 'none', maxHeight: 'none' }} />;
              })()}
            </div>
          </div>
          <div className="flex items-center gap-[10px] mb-4 px-5">
            <span className="text-[14px] text-t3">−</span>
            <input type="range" min="1" max="4" step="0.05" value={crop.zoom} onChange={e => setCrop(p => clampMobileCrop({ ...p, zoom: parseFloat(e.target.value) }))} className="flex-1" style={{ accentColor: K.p }} />
            <span className="text-[14px] text-t3">+</span>
          </div>
          <div className="flex gap-2">
            <Btn variant="secondary" full onClick={() => setCrop(p => ({ ...p, src: null, file: null, zoom: 1, offsetX: 0, offsetY: 0 }))} disabled={busy}>Change Photo</Btn>
            <Btn full icon={CheckCircle} onClick={doSave} disabled={busy}>{busy ? "Saving…" : "Save Photo"}</Btn>
          </div>
        </div>
      )}
    </div>
  </div>;
}

export const Profile = ({ me, onLogout, onRefreshMe }) => {
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "", showCur: false, showNext: false, showConfirm: false });
  const [pwdMsg, setPwdMsg] = useState(""); const [pwdErr, setPwdErr] = useState(""); const [pwdSaving, setPwdSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false); const [pushSaving, setPushSaving] = useState(false); const [pushMsg, setPushMsg] = useState("");
  const [testSaving, setTestSaving] = useState(false);
  const [reminderCfg, setReminderCfg] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoMsg, setPhotoMsg] = useState("");
  const e = me?.employee || {};
  const u = me?.user || {};
  const photo = e.profile_photo;
  const pushSupported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  useEffect(() => {
    api.get('/api/push/subscription-status').then(d => setPushOn(!!d.subscribed)).catch(() => {});
    api.get('/api/push/reminder-config').then(d => setReminderCfg(d)).catch(() => {});
  }, []);

  const togglePush = async () => {
    setPushSaving(true); setPushMsg("");
    try {
      if (!pushOn) {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') { setPushMsg("Notifications were blocked. Enable them in your browser/app settings."); setPushSaving(false); return; }
        await subscribePush(); setPushOn(true); setPushMsg("Reminders enabled on this device.");
      } else {
        await unsubscribePush(); setPushOn(false); setPushMsg("Reminders disabled.");
      }
    } catch (er) { setPushMsg("Failed: " + (er.message || "Unknown error")); }
    finally { setPushSaving(false); setTimeout(() => setPushMsg(""), 4000); }
  };

  const sendTest = async () => {
    setTestSaving(true); setPushMsg("");
    try { const r = await api.post('/api/push/test', {}); setPushMsg(r.sent > 0 ? `Test sent to ${r.sent} device(s)` : "No active subscription found"); }
    catch (er) { setPushMsg("Test failed: " + (er.message || "")); }
    finally { setTestSaving(false); setTimeout(() => setPushMsg(""), 4000); }
  };

  const changePwd = async () => {
    setPwdErr(""); setPwdMsg("");
    if (pwd.next !== pwd.confirm) return setPwdErr("Passwords do not match");
    if (pwd.next.length < 8) return setPwdErr("New password must be 8+ characters");
    if (!/[A-Za-z]/.test(pwd.next) || !/\d/.test(pwd.next)) return setPwdErr("Must contain letter + number");
    if (pwd.current === pwd.next) return setPwdErr("New password must differ from current");
    setPwdSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: pwd.current, new_password: pwd.next });
      setPwd({ current: "", next: "", confirm: "", showCur: false, showNext: false, showConfirm: false }); setPwdMsg("Password changed successfully.");
    } catch (er) { setPwdErr(er.message || "Failed"); } finally { setPwdSaving(false); }
  };

  return <div className="px-4 pt-4 pb-[100px]">
    <Card className="mb-[14px]" style={{ padding: 24 }}>
      <div className="text-center">
        <div className="relative inline-block mb-3">
          {photo ? <img src={uploadUrl(photo)} alt={e.name} className="w-[88px] h-[88px] rounded-full object-cover border-[3px] border-p" />
            : <div className="w-[88px] h-[88px] rounded-full flex items-center justify-center text-[30px] font-bold text-white" style={{ background: `linear-gradient(145deg,${K.p},${K.bl})` }}>{(e.name || "?").split(" ").map(s => s[0]).join("").slice(0, 2)}</div>}
          <button onClick={() => setShowPhotoModal(true)} className="absolute -bottom-[2px] -right-[2px] w-[28px] h-[28px] rounded-full bg-p flex items-center justify-center cursor-pointer p-0 border-2 border-s shadow-[0_2px_6px_rgba(0,0,0,0.2)]">
            <Camera size={13} color="#fff" />
          </button>
        </div>
        {showPhotoModal && <MobilePhotoUploadModal onClose={() => setShowPhotoModal(false)} onSaved={() => {
          onRefreshMe && onRefreshMe();
          setPhotoMsg("Photo updated successfully.");
          setTimeout(() => setPhotoMsg(""), 4000);
        }} />}
        <div className="text-[18px] font-extrabold text-t0">{e.name || "—"}</div>
        <div className="text-[14px] text-t3 mt-1">{u.employee_id} · {e.role_title || "—"}</div>
        <div className="flex justify-center gap-[6px] mt-[10px]">
          {e.department_name && <Pill c={K.bl}>{e.department_name}</Pill>}
          {e.office_city && <Pill c={K.p}>{e.office_city}</Pill>}
        </div>
      </div>
    </Card>

    {photoMsg && <div className="px-[10px] py-2 mb-[14px] bg-gb text-g rounded-lg text-[14px] flex items-center gap-[6px] justify-center"><CheckCircle size={13} />{photoMsg}</div>}

    <Card className="mb-[14px]">
      <div className="text-[14px] text-t3 mb-1">Email</div>
      <div className="text-[15px] text-t0 mb-3">{e.email || u.username || "—"}</div>
      <div className="text-[14px] text-t3 mb-1">Phone</div>
      <div className="text-[15px] text-t0 mb-3">{e.phone || "—"}</div>
      <div className="text-[14px] text-t3 mb-1">Designation</div>
      <div className="text-[15px] text-t0">{e.designation || e.role_title || "—"}</div>
    </Card>

    <Card className="mb-[14px]">
      <div className="flex items-center justify-between mb-[6px]">
        <div className="flex items-center gap-[6px]"><Bell size={16} color={K.p} /><span className="text-[15px] font-bold text-t0">Check-In Reminders</span></div>
        <label className={`inline-flex items-center gap-[6px] ${pushSupported && reminderCfg?.enabled ? "cursor-pointer opacity-100" : "cursor-not-allowed opacity-55"}`}>
          <input type="checkbox" checked={pushOn} disabled={!pushSupported || pushSaving || !reminderCfg?.enabled} onChange={togglePush} />
          <span className="text-[14px] text-t1">{pushSaving ? "…" : pushOn ? "On" : "Off"}</span>
        </label>
      </div>
      <div className="text-[14px] text-t3 mb-[6px]">
        {!pushSupported ? "Push notifications not supported on this browser. Add to Home Screen on iOS first."
          : !reminderCfg?.enabled ? "Reminders are currently disabled by HR."
          : reminderCfg ? `HR sends a daily ping at ${reminderCfg.time} IST if you haven't checked in yet.` : "Loading…"}
      </div>
      {pushOn && reminderCfg?.enabled && <Btn variant="secondary" full onClick={sendTest} disabled={testSaving} icon={Bell} style={{ padding: "10px 14px", fontSize: 13 }}>{testSaving ? "Sending…" : "Send Test Notification"}</Btn>}
      {pushMsg && <div className="px-[10px] py-2 mt-2 bg-bg2 text-t1 rounded-lg text-[14px]">{pushMsg}</div>}
    </Card>

    <Card className="mb-[14px]">
      <div className="flex items-center gap-[6px] mb-[6px]"><Lock size={16} color={K.p} /><span className="text-[15px] font-bold text-t0">Change Password</span></div>
      <div className="text-[14px] text-t3 mb-3">8+ chars with letter and number</div>
      {pwdMsg && <div className="px-[10px] py-2 bg-gb text-g rounded-lg text-[14px] mb-[10px] flex items-center gap-[6px]"><CheckCircle size={13} />{pwdMsg}</div>}
      {pwdErr && <div className="px-[10px] py-2 bg-rb text-r rounded-lg text-[14px] mb-[10px] flex items-center gap-[6px]"><AlertTriangle size={13} />{pwdErr}</div>}
      <Field label="Current Password">
        <div className="relative">
          <TextInput className="pr-9" type={pwd.showCur ? "text" : "password"} value={pwd.current} onChange={ev => setPwd(p => ({ ...p, current: ev.target.value, msg: "", err: "" }))} />
          <button type="button" onClick={() => setPwd(p => ({ ...p, showCur: !p.showCur }))} aria-label={pwd.showCur ? "Hide password" : "Show password"}
            className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1">
            {pwd.showCur ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </Field>
      <Field label="New Password">
        <div className="relative">
          <TextInput className="pr-9" type={pwd.showNext ? "text" : "password"} value={pwd.next} onChange={ev => setPwd(p => ({ ...p, next: ev.target.value }))} />
          <button type="button" onClick={() => setPwd(p => ({ ...p, showNext: !p.showNext }))} aria-label={pwd.showNext ? "Hide password" : "Show password"}
            className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1">
            {pwd.showNext ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </Field>
      <Field label="Confirm New Password">
        <div className="relative">
          <TextInput className="pr-9" type={pwd.showConfirm ? "text" : "password"} value={pwd.confirm} onChange={ev => setPwd(p => ({ ...p, confirm: ev.target.value }))} />
          <button type="button" onClick={() => setPwd(p => ({ ...p, showConfirm: !p.showConfirm }))} aria-label={pwd.showConfirm ? "Hide password" : "Show password"}
            className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1">
            {pwd.showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </Field>
      <Btn full icon={Lock} onClick={changePwd} disabled={pwdSaving || !pwd.current || !pwd.next || !pwd.confirm}>{pwdSaving ? "Updating…" : "Update Password"}</Btn>
    </Card>

    <Btn full variant="danger" icon={LogOut} onClick={onLogout}>Sign Out</Btn>
    <div className="text-center mt-4 text-[14px] text-t3">HRMS Mobile · v1.0</div>
  </div>;
};
